/**
 * Structured product description for admin + storefront.
 * Stored as JSON array on product.descriptionRich (backend) or synced plain text in description.
 */

export const DESCRIPTION_MODES = {
  SIMPLE: 'simple',
  STRUCTURED: 'structured',
};

const DEFAULT_TEXT_COLOR = '#333333';

function uid() {
  return `dblk_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function createBlock(type) {
  const base = { id: uid(), type, textColor: DEFAULT_TEXT_COLOR };
  if (type === 'heading') {
    return { ...base, text: '', highlight: false, bold: false };
  }
  if (type === 'paragraph') {
    return { ...base, text: '', bold: false };
  }
  if (type === 'bullets') {
    return { ...base, items: [''], bold: false };
  }
  return { ...base, type: 'paragraph', text: '', bold: false };
}

export function normalizeDescriptionBlock(b) {
  if (!b || typeof b !== 'object') return null;
  const type = b.type === 'heading' || b.type === 'paragraph' || b.type === 'bullets' ? b.type : 'paragraph';
  const textColor =
    typeof b.textColor === 'string' && /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(b.textColor.trim())
      ? b.textColor.trim()
      : DEFAULT_TEXT_COLOR;
  const id = typeof b.id === 'string' && b.id ? b.id : uid();
  if (type === 'bullets') {
    const items = Array.isArray(b.items)
      ? b.items.map((x) => (typeof x === 'string' ? x : String(x ?? '')))
      : [''];
    return { id, type: 'bullets', items, textColor, bold: Boolean(b.bold) };
  }
  if (type === 'heading') {
    return {
      id,
      type: 'heading',
      text: typeof b.text === 'string' ? b.text : String(b.text ?? ''),
      highlight: Boolean(b.highlight),
      bold: Boolean(b.bold),
      textColor,
    };
  }
  return {
    id,
    type: 'paragraph',
    text: typeof b.text === 'string' ? b.text : String(b.text ?? ''),
    textColor,
    bold: Boolean(b.bold),
  };
}

export function normalizeDescriptionBlocks(blocks) {
  if (!Array.isArray(blocks)) return [];
  return blocks.map(normalizeDescriptionBlock).filter(Boolean);
}

export function parseDescriptionRich(raw) {
  if (raw == null || raw === '') return null;
  if (Array.isArray(raw)) {
    const n = normalizeDescriptionBlocks(raw);
    return n.length > 0 ? n : null;
  }
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      if (Array.isArray(p)) {
        const n = normalizeDescriptionBlocks(p);
        return n.length > 0 ? n : null;
      }
    } catch {
      return null;
    }
  }
  if (typeof raw === 'object' && raw.blocks && Array.isArray(raw.blocks)) {
    const n = normalizeDescriptionBlocks(raw.blocks);
    return n.length > 0 ? n : null;
  }
  return null;
}

export function blocksToPlainText(blocks) {
  if (!Array.isArray(blocks) || blocks.length === 0) return '';
  const parts = [];
  for (const b of blocks) {
    if (!b || !b.type) continue;
    if (b.type === 'heading' || b.type === 'paragraph') {
      const t = (b.text || '').trim();
      if (t) parts.push(t);
    } else if (b.type === 'bullets' && Array.isArray(b.items)) {
      b.items.forEach((line) => {
        const t = (line || '').trim();
        if (t) parts.push(`• ${t}`);
      });
    }
  }
  return parts.join('\n\n');
}

export function hasRenderableRichContent(blocks) {
  if (!Array.isArray(blocks) || blocks.length === 0) return false;
  return blocks.some((b) => {
    if (!b) return false;
    if (b.type === 'heading' || b.type === 'paragraph') return Boolean((b.text || '').trim());
    if (b.type === 'bullets' && Array.isArray(b.items)) {
      return b.items.some((line) => (line || '').trim());
    }
    return false;
  });
}

/** Appended to `description` when API does not persist `descriptionRich` (fallback storage). */
export const RICH_DESCRIPTION_EMBED_MARKER = '\n\n__SW_RICH_JSON__';

/**
 * Save plain summary + JSON blocks inside the main description string (works without backend field).
 */
export function embedRichDescriptionForStorage(plainText, blocks) {
  const n = normalizeDescriptionBlocks(blocks || []);
  if (!hasRenderableRichContent(n)) {
    return (plainText || '').trim();
  }
  const plain = (plainText || '').trim();
  return plain + RICH_DESCRIPTION_EMBED_MARKER + JSON.stringify(n);
}

export function parseStoredEmbeddedDescription(description) {
  if (typeof description !== 'string') {
    return { plain: '', rich: null };
  }
  const idx = description.indexOf(RICH_DESCRIPTION_EMBED_MARKER);
  if (idx === -1) {
    return { plain: description.trim(), rich: null };
  }
  const plain = description.slice(0, idx).trim();
  const jsonPart = description.slice(idx + RICH_DESCRIPTION_EMBED_MARKER.length);
  try {
    const parsed = JSON.parse(jsonPart);
    const rich = normalizeDescriptionBlocks(Array.isArray(parsed) ? parsed : []);
    return { plain, rich: rich.length ? rich : null };
  } catch {
    return { plain: description.trim(), rich: null };
  }
}

export function stripEmbedFromDescriptionForDisplay(description) {
  if (typeof description !== 'string') return '';
  const idx = description.indexOf(RICH_DESCRIPTION_EMBED_MARKER);
  if (idx === -1) return description.trim();
  return description.slice(0, idx).trim();
}

/** Prefer API `descriptionRich`, else JSON embedded in `description`. */
export function mergeDescriptionRichSources(descriptionRichFromApi, descriptionField) {
  const fromApi = parseDescriptionRich(descriptionRichFromApi);
  if (fromApi && hasRenderableRichContent(fromApi)) return fromApi;
  const embedded = parseStoredEmbeddedDescription(typeof descriptionField === 'string' ? descriptionField : '');
  if (embedded.rich && hasRenderableRichContent(embedded.rich)) return embedded.rich;
  return null;
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Convert legacy JSON blocks to HTML for the HTML editor (edit migration). */
export function blocksToLegacyHtml(blocks) {
  const n = normalizeDescriptionBlocks(blocks || []);
  const parts = [];
  for (const b of n) {
    if (!b) continue;
    if (b.type === 'heading') {
      const t = escapeHtml((b.text || '').trim());
      if (!t) continue;
      const styles = [`color:${b.textColor || '#333333'}`];
      if (b.bold) styles.push('font-weight:800');
      if (b.highlight) {
        styles.push(
          'background:linear-gradient(135deg,rgba(212,175,55,0.18),rgba(212,175,55,0.05))',
          'padding:8px 10px',
          'border-left:4px solid #d4af37'
        );
      }
      parts.push(`<h4 style="${styles.join(';')}">${t}</h4>`);
    } else if (b.type === 'paragraph') {
      const t = escapeHtml((b.text || '').trim());
      if (!t) continue;
      const styles = [`color:${b.textColor || '#333333'}`];
      if (b.bold) styles.push('font-weight:700');
      parts.push(`<p style="${styles.join(';')}">${t}</p>`);
    } else if (b.type === 'bullets') {
      const items = (b.items || [])
        .map((x) => (typeof x === 'string' ? x : String(x ?? '')))
        .filter((x) => (x || '').trim());
      if (!items.length) continue;
      const styles = [`color:${b.textColor || '#333333'}`];
      if (b.bold) styles.push('font-weight:700');
      parts.push(
        `<ul style="${styles.join(';')}">${items
          .map((line) => `<li>${escapeHtml(line.trim())}</li>`)
          .join('')}</ul>`
      );
    }
  }
  return parts.join('');
}
